import { cn } from "../utils/cn";
import type { Difficulty } from "../types/common";

export type { Difficulty };

export interface DifficultyBadgeProps {
  difficulty: Difficulty;
  size?: "sm" | "md" | "lg";
  showIcon?: boolean;
  className?: string;
}

// ── Config ────────────────────────────────────────────────────────────────────

const CONFIG: Record<Difficulty, { label: string; bg: string; text: string; border: string }> = {
  easy: {
    label: "Easy",
    bg:     "bg-green-100 dark:bg-green-900/30",
    text:   "text-green-700 dark:text-green-400",
    border: "border-green-200/60 dark:border-green-800/40",
  },
  moderate: {
    label: "Moderate",
    bg:     "bg-amber-100 dark:bg-amber-900/30",
    text:   "text-amber-700 dark:text-amber-400",
    border: "border-amber-200/60 dark:border-amber-800/40",
  },
  difficult: {
    label: "Difficult",
    bg:     "bg-orange-100 dark:bg-orange-900/30",
    text:   "text-orange-700 dark:text-orange-400",
    border: "border-orange-200/60 dark:border-orange-800/40",
  },
  extreme: {
    label: "Extreme",
    bg:     "bg-red-100 dark:bg-red-900/30",
    text:   "text-red-700 dark:text-red-400",
    border: "border-red-200/60 dark:border-red-800/40",
  },
};

const SIZE = {
  sm: { pill: "px-2 py-0.5 text-xs gap-1 rounded-md",    icon: 12 },
  md: { pill: "px-2.5 py-1 text-xs gap-1.5 rounded-lg",  icon: 14 },
  lg: { pill: "px-3 py-1.5 text-sm gap-2 rounded-xl",    icon: 16 },
};

// ── Mountain range SVG (peaks increase with difficulty) ───────────────────────

function MountainRange({ size, difficulty }: { size: number; difficulty: Difficulty }) {
  const paths: Record<Difficulty, string> = {
    easy:      "M1 13 Q4 9 7 11 Q10 7 13 10 Q16 6 19 9 L23 13 Z",
    moderate:  "M1 14 L5 8 L8 12 L12 5 L16 10 L19 12 L23 14 Z",
    difficult: "M1 14 L4 6 L7 11 L11 3 L15 9 L18 11 L23 14 Z",
    extreme:   "M1 14 L3 5 L6 10 L9 2 L12 8 L15 4 L18 9 L21 12 L23 14 Z",
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 16"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d={paths[difficulty]} />
    </svg>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function DifficultyBadge({
  difficulty,
  size = "md",
  showIcon = true,
  className,
}: DifficultyBadgeProps) {
  const { label, bg, text, border } = CONFIG[difficulty];
  const { pill, icon } = SIZE[size];

  return (
    <span
      className={cn(
        "inline-flex items-center font-semibold border",
        "transition-colors duration-150",
        bg, text, border, pill,
        className,
      )}
    >
      {showIcon && <MountainRange size={icon} difficulty={difficulty} />}
      {label}
    </span>
  );
}

DifficultyBadge.displayName = "DifficultyBadge";
